/**
 * Composable pour les routes utilisateur filtrées selon le rôle
 *
 * Ce composable centralise la logique de filtrage des routes
 * basée sur la configuration du rôle utilisateur. Il remplace
 * les filtres manuels dispersés dans les composants de navigation.
 *
 * @module common/composables/useUserRoutes
 */

import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAppRouter } from '@/router/composables/useAppRouter';
import { useLayout } from '@/layouts/composables/useLayout';
import type { NormalizedUserRoute } from '@/layouts/constants';

/**
 * Composable pour les routes utilisateur filtrées selon le rôle
 *
 * @returns Objet contenant les routes et des utilitaires
 *
 * @example
 * // Usage basique
 * const { displayRoutes, navigateToRoute } = useUserRoutes();
 *
 * @example
 * // Dans un template
 * <div v-for="route in displayRoutes" :key="route.name">
 *   {{ getRouteTitle(route) }}
 * </div>
 */
export function useUserRoutes() {
  const router = useRouter();
  const { getMenuRoutesByRoles, navigateWithRoleCheck } = useAppRouter();
  const { excludedRouteNames } = useLayout();

  // ============================================
  // Routes calculées
  // ============================================

  /**
   * Toutes les routes accessibles par l'utilisateur (non filtrées)
   */
  const userRoutes = computed<NormalizedUserRoute[]>(() => {
    const routes = getMenuRoutesByRoles();
    return routes.map((route) => ({
      name: route.name as string,
      path: route.path,
      meta: route.meta,
    }));
  });

  /**
   * Routes filtrées pour l'affichage dans les menus
   * (exclut les routes configurées dans RoleLayoutConfig)
   */
  const displayRoutes = computed<NormalizedUserRoute[]>(() => {
    return userRoutes.value.filter(
      (route) => !excludedRouteNames.value.some((excluded) => route.name.includes(excluded))
    );
  });

  /**
   * Nombre total de routes disponibles
   */
  const totalRoutes = computed(() => userRoutes.value.length);

  /**
   * Nombre de routes affichées (après filtrage)
   */
  const displayedRoutesCount = computed(() => displayRoutes.value.length);

  /**
   * Indique s'il y a des routes à afficher
   */
  const hasRoutes = computed(() => displayRoutes.value.length > 0);

  // ============================================
  // Navigation
  // ============================================

  /**
   * Navigue vers une route
   *
   * @param route - La route vers laquelle naviguer
   */
  function navigateToRoute(route: NormalizedUserRoute): void {
    if (route.path) {
      router.push(route.path);
    }
  }

  /**
   * Navigue vers une route par son nom (avec vérification de rôle)
   *
   * @param routeName - Le nom de la route
   */
  function navigateToRouteName(routeName: string): void {
    navigateWithRoleCheck(routeName);
  }

  /**
   * Vérifie si une route est actuellement active
   *
   * @param route - La route à vérifier
   * @returns true si la route est active
   */
  function isRouteActive(route: NormalizedUserRoute): boolean {
    return router.currentRoute.value.path === route.path;
  }

  /**
   * Vérifie si une route (par chemin) est actuellement active
   *
   * @param path - Le chemin de la route
   * @returns true si le chemin correspond à la route active
   */
  function isPathActive(path: string): boolean {
    return router.currentRoute.value.path === path;
  }

  // ============================================
  // Utilitaires
  // ============================================

  /**
   * Récupère l'icône d'une route
   *
   * @param route - La route
   * @returns Le nom de l'icône ou une icône par défaut
   */
  function getRouteIcon(route: NormalizedUserRoute): string {
    return route.meta?.icon ?? 'apps-outline';
  }

  /**
   * Récupère le titre (clé de traduction) d'une route
   *
   * @param route - La route
   * @returns La clé de traduction du titre ou le nom de la route
   */
  function getRouteTitle(route: NormalizedUserRoute): string {
    return route.meta?.title ?? route.name;
  }

  /**
   * Trouve une route par son nom
   *
   * @param name - Le nom de la route
   * @returns La route ou undefined
   */
  function findRouteByName(name: string): NormalizedUserRoute | undefined {
    return userRoutes.value.find((route) => route.name === name);
  }

  /**
   * Trouve une route par son chemin
   *
   * @param path - Le chemin de la route
   * @returns La route ou undefined
   */
  function findRouteByPath(path: string): NormalizedUserRoute | undefined {
    return userRoutes.value.find((route) => route.path === path);
  }

  /**
   * Vérifie si une route est exclue du menu
   *
   * @param route - La route à vérifier
   * @returns true si la route est exclue
   */
  function isRouteExcluded(route: NormalizedUserRoute): boolean {
    return excludedRouteNames.value.some((excluded) => route.name.includes(excluded));
  }

  // ============================================
  // Return
  // ============================================

  return {
    // Routes
    userRoutes,
    displayRoutes,
    totalRoutes,
    displayedRoutesCount,
    hasRoutes,

    // Navigation
    navigateToRoute,
    navigateToRouteName,
    isRouteActive,
    isPathActive,

    // Utilitaires
    getRouteIcon,
    getRouteTitle,
    findRouteByName,
    findRouteByPath,
    isRouteExcluded,
  };
}
